import { Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { faArrowDown, faArrowUp, faPlusCircle, faSort, faTrash } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, first } from 'rxjs';
import { indicate } from 'src/app/operators';
import { MultiSelectFilterComponent } from 'src/app/search/filters/multi-select/multi-select-filter/multi-select-filter.component';
import { EsraPipingAPIClient, ReleaseForDashboardViewModel } from 'src/app/shared/models/autogenerated-piping';
import { CreateReleaseWizardComponent } from '../../releases/create-release-wizard/create-release-wizard.component';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { UserService } from 'src/app/core/services/user.service';
import { Constants } from 'src/app/shared/constants/constants';

// Checkbox-only: options is a flat list of selectable string values, rendered via app-multi-select-filter.
// The Creation Date / Release Date filters are NOT part of this array -- they're continuous ranges (FormGroup<{start,end}>),
// not a finite option list, so they're hand-rolled mat-expansion-panels in the template instead (see releases-tab.component.html).
// To reuse this pattern elsewhere: copy the array + app-multi-select-filter loop for checkbox filters, and copy the
// date-range mat-expansion-panel block (+ its FormGroup/onDateRangeChange/clear methods) separately for date filters.
// To unify them instead: add a `type: 'checkbox' | 'dateRange'` discriminator to Filter and teach
// app-multi-select-filter to conditionally render a date-range-picker -- bigger change, affects other pages using that component.
class Filter {
  filterName: string;
  options: string[];

  constructor(filterName: string, options: string[]) {
    this.filterName = filterName;
    this.options = options;
  }
}

/**
 * Ordered status definitions for the summary bar, row marker, and badge.
 * `code` matches the integer `statusCode` returned by the API (e.g. `0` = Draft).
 * `label` is the human-readable display string shown in the UI.
 * Workflow order mirrors the revision status pipeline.
 */
const STATUS_DEFINITIONS: ReadonlyArray<{ code: number; label: string; cssClass: string }> = [
  { code: 0, label: 'Draft',            cssClass: 'status-draft' },
  { code: 1, label: 'In Review',        cssClass: 'status-in-review' },
  { code: 2, label: 'Ready to Publish', cssClass: 'status-ready-to-publish' },
  { code: 3, label: 'Published',        cssClass: 'status-published' },
];

@Component({
    selector: 'app-releases-tab',
    templateUrl: './releases-tab.component.html',
    styleUrls: ['./releases-tab.component.scss'],
    standalone: false
})
export class ReleasesTabComponent implements OnInit {
  // ── Icons ─────────────────────────────────────────────────────────────
  readonly faArrowDown = faArrowDown;
  readonly faArrowUp = faArrowUp;
  readonly faPlusCircle = faPlusCircle;  /** Neutral sort indicator shown on columns that are not the active sort column. */
  readonly faSort = faSort;
  readonly faTrash = faTrash;
  /** Ordered list of known statuses; drives the summary bar and CSS classes. */
  readonly statusDefinitions = STATUS_DEFINITIONS;

  // ── State ─────────────────────────────────────────────────────────────
  /** Full unfiltered list fetched from the API. Never mutated after initial load. */
  private allReleases: ReleaseForDashboardViewModel[] = [];

  /** Releases after all filters and search are applied; the source for pagination. */
  private filteredReleases: ReleaseForDashboardViewModel[] = [];

  dataSource: MatTableDataSource<ReleaseForDashboardViewModel> = new MatTableDataSource([]);

  columnNames: string[] = [
    'statusMarker', 'name', 'author', 'bu', 'application', 'component',
    'revision', 'quantity', 'creationDate', 'releaseDate', 'status', 'actions'
  ];

  // ── Filters ───────────────────────────────────────────────────────────
  filters: Filter[] = [];

  /**
   * Tracks the currently selected options for each sidebar filter.
   * Keys are filter names (e.g. 'Status', 'BU'); values are the selected option sets.
   */
  private filterSelections: Map<string, Set<string>> = new Map();

  searchText: string = '';

  // ── Date range filters ───────────────────────────────────────────────
  /** Backs the Creation Date range picker; control names `start`/`end` are required by `matStartDate`/`matEndDate`. */
  creationDateRange: FormGroup<{ start: FormControl<Date | null>; end: FormControl<Date | null> }> = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  /** Backs the Release Date range picker. */
  releaseDateRange: FormGroup<{ start: FormControl<Date | null>; end: FormControl<Date | null> }> = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  // ── Status counts ─────────────────────────────────────────────────────
  /** Count per `statusCode` integer, recomputed on every filter change. */
  statusCounts: { [code: number]: number } = {};

  // ── Sort state ────────────────────────────────────────────────────────
  /** Column currently driving the sort order. Defaults to creation date. */
  activeSortColumn: string = 'creationDate';
  /** When true, the active column sorts ascending; false = descending (default for dates). */
  sortAscending: boolean = false;

  // ── Pagination ────────────────────────────────────────────────────────
  paginatorTotalCount: number = 0;
  currentPage: number = 0;
  pageSize: number = 10;
  pageSizes: number[] = [10, 25, 50, 100];

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private currentUserOid: string = '';

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChildren(MultiSelectFilterComponent) childFilters: QueryList<MultiSelectFilterComponent>;

  constructor(
    private readonly esraPipingAPIClient: EsraPipingAPIClient,
    private readonly dialog: MatDialog,
    private readonly msalService: MsalService,
    private readonly router: Router,
    private readonly snackBarService: SnackBarService,
    private readonly userService: UserService,
  ) {}

  ngOnInit(): void {
    this.currentUserOid = ((this.msalService.instance.getActiveAccount()?.idTokenClaims?.['oid'] as string | undefined) ?? '').toLowerCase();
    this.loadReleases();
  }

  /** Opens the Create New Release wizard dialog. Reloads the list on successful creation. */
  openCreateReleaseWizard(): void {
    const dialogRef = this.dialog.open(CreateReleaseWizardComponent, {
      panelClass: 'wide-modal',
      disableClose: true,
    });

    dialogRef.afterClosed().pipe(first()).subscribe((created: boolean) => {
      if (created) {
        this.loadReleases();
      }
    });
  }

  private loadReleases(): void {
    this.esraPipingAPIClient.listReleases()
      .pipe(indicate(this.loading$), first())
      .subscribe(releases => {
        this.allReleases = [...releases];
        this.initializeFilters(releases);
        this.applyAllFilters();
      });
  }

  /**
   * Builds the sidebar filter definitions from unique values in the loaded releases.
   * Called once after the initial load.
   */
  private initializeFilters(releases: ReleaseForDashboardViewModel[]): void {
    const unique = <T>(arr: T[]): T[] => [...new Set(arr)];
    const isDefined = (value: string | undefined): value is string => !!value;

    // Build the Status filter using known definition order and display labels.
    // Match on `statusCode` (integer) for reliability — the `status` string is
    // an undocumented raw enum name and may change.
    const presentStatusCodes = new Set(releases.map(r => r.statusCode).filter(c => c !== undefined && c !== null));
    const statusLabels: string[] = STATUS_DEFINITIONS
      .filter(sd => presentStatusCodes.has(sd.code))
      .map(sd => sd.label);

    const bus = unique(releases.map(r => r.businessUnit).filter(isDefined));
    const applications = unique(releases.map(r => r.application).filter(isDefined));
    const components = unique(releases.map(r => r.component).filter(isDefined));
    const authors = unique(releases.map(r => r.author).filter(isDefined));

    if (statusLabels.length > 0)  { this.filters.push(new Filter('Status',      statusLabels)); }
    if (authors.length > 0)       { this.filters.push(new Filter('Author',      authors)); }
    if (bus.length > 0)           { this.filters.push(new Filter('BU',           bus)); }
    if (applications.length > 0)  { this.filters.push(new Filter('Application',  applications)); }
    if (components.length > 0)    { this.filters.push(new Filter('Component',    components)); }
  }

  /**
   * Handles a change in any sidebar filter checkbox.
   * Receives the full set of currently selected values for the filter.
   */
  onCheckboxChange(selectedValues: Set<string>, filter: Filter): void {
    if (selectedValues.size === 0) {
      this.filterSelections.delete(filter.filterName);
    } else {
      this.filterSelections.set(filter.filterName, new Set(selectedValues));
    }
    this.currentPage = 0;
    this.applyAllFilters();
  }

  clearAllFilters(): void {
    this.childFilters.forEach(childFilter => childFilter.clearSelections());
    this.filterSelections.clear();
    this.searchText = '';
    this.creationDateRange.reset();
    this.releaseDateRange.reset();
    this.currentPage = 0;
    this.applyAllFilters();
  }

  callSearch(term: string): void {
    this.searchText = term;
    this.currentPage = 0;
    this.applyAllFilters();
  }

  /** Handles a change to either the creation or publish date range picker. */
  onDateRangeChange(): void {
    this.currentPage = 0;
    this.applyAllFilters();
  }

  clearCreationDateRange(): void {
    this.creationDateRange.reset();
    this.onDateRangeChange();
  }

  clearReleaseDateRange(): void {
    this.releaseDateRange.reset();
    this.onDateRangeChange();
  }

  /**
   * Applies all active sidebar filters and the search text to `allReleases`,
   * then updates the paginated data source.
   * All filtering is done client-side — the full list is expected to be small.
   */
  private applyAllFilters(): void {
    let result: ReleaseForDashboardViewModel[] = [...this.allReleases];

    // Apply each sidebar filter; only active (non-empty) selections narrow the set
    for (const [filterName, selectedValues] of this.filterSelections) {
      if (selectedValues.size === 0) { continue; }
      result = result.filter(r => {
        switch (filterName) {
          // Status filter stores display labels; match via statusCode for accuracy.
          case 'Status':      return selectedValues.has(this.getStatusLabel(r.statusCode));
          case 'Author':      return selectedValues.has(r.author ?? '');
          case 'BU':          return selectedValues.has(r.businessUnit ?? '');
          case 'Application': return selectedValues.has(r.application ?? '');
          case 'Component':   return selectedValues.has(r.component ?? '');
          default:            return true;
        }
      });
    }

    // Apply free-text search across the main visible fields
    if (this.searchText) {
      const term: string = this.searchText.toLowerCase();
      result = result.filter(r =>
        [r.name, r.businessUnit, r.application, r.component, r.revision, r.status]
          .some(v => v?.toLowerCase().includes(term))
      );
    }

    const creationRange = this.creationDateRange.value;
    if (creationRange.start || creationRange.end) {
      result = result.filter(r => this.isDateInRange(r.creationDate, creationRange.start ?? null, creationRange.end ?? null));
    }

    const releaseRange = this.releaseDateRange.value;
    if (releaseRange.start || releaseRange.end) {
      result = result.filter(r => this.isDateInRange(r.releaseDate, releaseRange.start ?? null, releaseRange.end ?? null));
    }

    this.filteredReleases = result;
    this.paginatorTotalCount = result.length;
    this.setStatusCounts(result);
    this.applySortToResult(this.filteredReleases);
    this.setPagedData();
  }

  /**
   * Checks whether `date` falls within the inclusive [from, to] range, comparing by calendar
   * day only (ignoring time-of-day) so the "To" bound includes the entirety of that day.
   */
  private isDateInRange(date: Date | undefined, from: Date | null, to: Date | null): boolean {
    if (!date) {
      return false;
    }
    const dateOnly: number = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    if (from) {
      const fromOnly: number = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
      if (dateOnly < fromOnly) {
        return false;
      }
    }
    if (to) {
      const toOnly: number = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
      if (dateOnly > toOnly) {
        return false;
      }
    }
    return true;
  }

  /** Recomputes `statusCounts` (keyed by `statusCode`) from the provided (post-filter) release list. */
  private setStatusCounts(releases: ReleaseForDashboardViewModel[]): void {
    this.statusCounts = {};
    for (const release of releases) {
      const code: number = release.statusCode ?? -1;
      this.statusCounts[code] = (this.statusCounts[code] ?? 0) + 1;
    }
  }

  /**
   * Maps a `statusCode` integer to its human-readable display label.
   * Falls back to the raw code string when no mapping exists.
   */
  getStatusLabel(code: number | undefined): string {
    return STATUS_DEFINITIONS.find(sd => sd.code === code)?.label ?? String(code ?? '');
  }

  /**
   * Returns the percentage width string for a status bar segment.
   * `statusCode` is the integer code returned by the API.
   */
  getStatusBarWidth(statusCode: number): string {
    const total: number = this.filteredReleases.length;
    if (total === 0) { return '0%'; }
    const count: number = this.statusCounts[statusCode] ?? 0;
    return `${(count / total) * 100}%`;
  }

  /** Returns the CSS row-marker class for the given `statusCode` integer. */
  getStatusRowClass(statusCode: number | undefined): string {
    switch (statusCode) {
      case 0: return 'status-row-draft';
      case 1: return 'status-row-in-review';
      case 2: return 'status-row-ready-to-publish';
      case 3: return 'status-row-published';
      default: return '';
    }
  }

  sortTable(columnName: string): void {
    // Toggle direction when re-clicking the active column; start ascending for a new column.
    if (this.activeSortColumn === columnName) {
      this.sortAscending = !this.sortAscending;
    } else {
      this.activeSortColumn = columnName;
      this.sortAscending = true;
    }
    this.currentPage = 0;
    this.applyAllFilters();
  }

  /**
   * Sorts `result` in-place according to `activeSortColumn` and `sortAscending`.
   * Called from `applyAllFilters()` so the sort is always re-applied after filtering.
   */
  private applySortToResult(result: ReleaseForDashboardViewModel[]): void {
    const asc: boolean = this.sortAscending;
    const compareStr = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0) * (asc ? 1 : -1);
    const compareDate = (a: Date | undefined, b: Date | undefined): number => {
      const da: number = a?.getTime() ?? 0;
      const db: number = b?.getTime() ?? 0;
      return (da - db) * (asc ? 1 : -1);
    };

    switch (this.activeSortColumn) {
      case 'name':         result.sort((a, b) => compareStr(a.name ?? '', b.name ?? '')); break;
      case 'author':       result.sort((a, b) => compareStr(a.author ?? '', b.author ?? '')); break;
      case 'bu':           result.sort((a, b) => compareStr(a.businessUnit ?? '', b.businessUnit ?? '')); break;
      case 'application':  result.sort((a, b) => compareStr(a.application ?? '', b.application ?? '')); break;
      case 'component':    result.sort((a, b) => compareStr(a.component ?? '', b.component ?? '')); break;
      case 'creationDate': result.sort((a, b) => compareDate(a.creationDate, b.creationDate)); break;
      case 'releaseDate':  result.sort((a, b) => compareDate(a.releaseDate, b.releaseDate)); break;
      case 'status':       result.sort((a, b) => ((a.statusCode ?? 0) - (b.statusCode ?? 0)) * (asc ? 1 : -1)); break;
    }
  }

  updatePage(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.setPagedData();
  }

  openReleaseDetails(release: ReleaseForDashboardViewModel): void {
    if (!release.id || !this.canOpenReleaseDetails(release)) {
      return;
    }

    this.router.navigate(['/piping/releases', release.id]);
  }

  /**
   * Determines if the current user is allowed to navigate into a release's details page.
   * `canCurrentUserOpen` is computed by the backend and covers authorship, any release
   * role membership (owner/approver/reviewer), and the Admin/Piping.Revision.Reader app
   * roles. The checks below are a client-side fallback for stale/cached release data.
   */
  canOpenReleaseDetails(release: ReleaseForDashboardViewModel): boolean {
    const releaseOwnerOid: string = (release.authorOid ?? '').toLowerCase();
    if (release.canCurrentUserOpen) {
      return true;
    }
    if(releaseOwnerOid.length > 0 && this.currentUserOid.length > 0 && releaseOwnerOid === this.currentUserOid){
      return true;
    }
    return this.userService.isRoleExists(Constants.admin) || this.userService.isRoleExists(Constants.pipingRevisionReader);
  }

  private setPagedData(): void {
    const start: number = this.currentPage * this.pageSize;
    const end: number = start + this.pageSize;
    this.dataSource = new MatTableDataSource(this.filteredReleases.slice(start, end));
  }

  /**
   * Determines if the current user can remove a release.
   * User must be the author and the release must be in Draft status (code 0).
   */
  canRemoveRelease(release: ReleaseForDashboardViewModel): boolean {
    const releaseOwnerOid: string = (release.authorOid ?? '').toLowerCase();
    const isAuthor: boolean = releaseOwnerOid.length > 0 && this.currentUserOid.length > 0 && releaseOwnerOid === this.currentUserOid;
    const isDraft: boolean = release.statusCode === 0;
    return isAuthor && isDraft;
  }

  /**
   * Removes a release after confirming with the user.
   * Only available for Draft releases owned by the current user.
   */
  removeRelease(event: Event, release: ReleaseForDashboardViewModel): void {
    event.stopPropagation();

    if (!this.canRemoveRelease(release) || !release.id) {
      return;
    }

    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: `Are you sure you want to delete the release "${release.name}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }
    });

    confirmDialogRef.afterClosed().pipe(first()).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.performRemoveRelease(release.id!);
      }
    });
  }

  /**
   * Calls the API to remove the release by id.
   */
  private performRemoveRelease(releaseId: string): void {
    this.esraPipingAPIClient.removeReleaseById(releaseId)
      .pipe(indicate(this.loading$), first())
      .subscribe({
        next: () => {
          this.snackBarService.showInfo('Release deleted successfully.');
          this.loadReleases();
        },
        error: (err: any) => {
          this.snackBarService.showError('Failed to delete release. Please try again.');
          console.error('Remove release error:', err);
        }
      });
  }
}
